import { Button, Heading, Input, LuIcon, Separator, Text } from "@overmap-ai/blocks"
import { type User } from "@overmap-ai/common"
import {
	selectOrganizationAccessUserMapping,
	selectProjectById,
	selectUsersOfOrganization,
	selectUsersOfProject,
} from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi } from "@tanstack/react-router"
import { memo, useCallback, useMemo } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { AddUserDropdownMenu } from "../../components/DropdownMenus/AddUserDropdownMenu"
import { useProjectUserTable, useProjectUserTableColumns } from "../../components/Tables"
import { Table } from "../../components/Tables/Table"
import { userFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/settings/projects/$projectId/members")

export const ProjectSettingsMembersView = memo(() => {
	const { projectId, organizationId } = routeApi.useParams()

	const project = useAppSelector(selectProjectById(projectId))

	if (!project) {
		throw new Error(`Expected a project, got ${project}`)
	}

	const organizationUsers = useShallowEqaulSelector(selectUsersOfOrganization(organizationId))
	const projectUsers = useShallowEqaulSelector(selectUsersOfProject(projectId))
	const organizationAccessByUser = useAppSelector(selectOrganizationAccessUserMapping)

	const nonProjectUsers = useMemo(() => {
		const projectUserIds = new Set(projectUsers.map((user) => user.id))
		return organizationUsers.filter((user) => !projectUserIds.has(user.id))
	}, [organizationUsers, projectUsers])

	const handleAddUserToProject = useCallback(
		(user: User) => {
			void sdk.projectAccesses.add({
				user: user.id,
				project: projectId,
				organization_access: organizationAccessByUser[user.id].uuid,
			})
		},
		[organizationAccessByUser, projectId],
	)

	const handleAddAllUsersToProject = useCallback(
		(users: User[]) => {
			void sdk.projectAccesses.bulkAdd(
				users.map((user) => ({
					user: user.id,
					project: projectId,
					organization_access: organizationAccessByUser[user.id].uuid,
				})),
			)
		},
		[organizationAccessByUser, projectId],
	)

	const [filteredProjectUsers, filterValue, setFilterValue] = useTextFilter(projectUsers, userFilterCallback)

	const columns = useProjectUserTableColumns(project)

	const table = useProjectUserTable({
		data: filteredProjectUsers,
		columns,
		enableHiding: false,
	})

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex flex-col gap-1">
					<Heading size="xl">{project.name} members</Heading>

					<Text size="sm" accentColor="base">
						Manage project members.
					</Text>
				</div>

				<Separator size="full" />

				<div className="flex min-h-0 grow flex-col gap-2">
					<div className="flex w-full items-center justify-between gap-2">
						<Input.Root variant="outline">
							<Input.Slot>
								<LuIcon icon={modelIconMapping.search} />
							</Input.Slot>
							<Input.Field
								placeholder="Search"
								value={filterValue}
								onChange={useChangeEventHandler(setFilterValue)}
							/>
						</Input.Root>

						<AddUserDropdownMenu
							users={nonProjectUsers}
							onSelectUser={handleAddUserToProject}
							onSelectAllUsers={handleAddAllUsersToProject}
							disabled={nonProjectUsers.length === 0}
							size="sm"
						>
							<Button variant="surface" size="sm">
								<LuIcon icon={modelIconMapping.add} />
								Add
							</Button>
						</AddUserDropdownMenu>
					</div>

					<div className="min-h-0 w-full grow overflow-auto">
						<Table
							size="sm"
							variant="surface"
							className="w-full"
							table={{ ...table }}
							interactive={false}
							emptyText="No users."
						/>
					</div>
				</div>
			</section>
		</ViewContainer>
	)
})
ProjectSettingsMembersView.displayName = "ProjectSettingsMembersView"
